import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/StoreDataContext";
import { resolveImageUrl } from "../lib/images";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag } from "lucide-react";

export default function Cart() {
    const { items, updateQty, removeItem, total } = useCart();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const freeThreshold = settings?.free_shipping_threshold ?? 50;
    const shippingCost = total >= freeThreshold ? 0 : 5;

    if (items.length === 0) {
        return (
            <div className="px-6 md:px-12 lg:px-20 pt-32 pb-20 text-center" data-testid="cart-page-empty">
                <ShoppingBag strokeWidth={1.5} className="w-12 h-12 mx-auto text-rose" />
                <h1 className="font-serif text-5xl text-charcoal mt-6">Tu carrito está vacío</h1>
                <p className="text-taupe mt-4">Aún no has añadido ninguna pieza.</p>
                <Link
                    to="/catalogo"
                    className="inline-block mt-8 bg-rose text-white hover:bg-rose-dark rounded-full px-8 py-3.5 transition-colors text-xs tracking-[0.2em] uppercase"
                    data-testid="empty-cart-shop-btn"
                >
                    Descubrir colección
                </Link>
            </div>
        );
    }

    return (
        <div className="px-6 md:px-12 lg:px-20 pt-32 pb-20" data-testid="cart-page">
            <h1 className="font-serif text-5xl sm:text-6xl text-charcoal mb-12">
                Tu <span className="italic">carrito.</span>
            </h1>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                    {items.map((it) => (
                        <div
                            key={it.product_id}
                            className="flex gap-4 bg-white rounded-xl border border-border-soft/40 p-4"
                            data-testid={`cart-item-${it.product_id}`}
                        >
                            <div className="w-24 h-24 rounded-lg overflow-hidden bg-blush shrink-0">
                                {it.image ? (
                                    <img src={resolveImageUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                                ) : null}
                            </div>
                            <div className="flex-1 flex flex-col justify-between">
                                <div className="flex justify-between gap-4">
                                    <div>
                                        <h3 className="font-serif text-xl text-charcoal">{it.name}</h3>
                                        <p className="text-sm text-rose mt-1">${it.price.toFixed(2)}</p>
                                    </div>
                                    <button
                                        onClick={() => removeItem(it.product_id)}
                                        className="text-taupe hover:text-rose p-1 h-fit"
                                        data-testid={`remove-item-${it.product_id}`}
                                        aria-label="Eliminar"
                                    >
                                        <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center border border-border-soft rounded-full">
                                        <button
                                            onClick={() => updateQty(it.product_id, it.quantity - 1)}
                                            className="p-2 hover:text-rose"
                                            data-testid={`qty-dec-${it.product_id}`}
                                        >
                                            <Minus strokeWidth={1.5} className="w-3 h-3" />
                                        </button>
                                        <span className="w-8 text-center text-sm">{it.quantity}</span>
                                        <button
                                            onClick={() => updateQty(it.product_id, it.quantity + 1)}
                                            className="p-2 hover:text-rose"
                                            data-testid={`qty-inc-${it.product_id}`}
                                        >
                                            <Plus strokeWidth={1.5} className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <p className="font-serif text-lg text-charcoal">
                                        ${(it.price * it.quantity).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl border border-border-soft/40 p-6 lg:sticky lg:top-28">
                        <h2 className="font-serif text-2xl text-charcoal">Resumen</h2>
                        <div className="mt-6 space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-taupe">Subtotal</span>
                                <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-taupe">Envío</span>
                                <span>{shippingCost === 0 ? "Gratis" : `$${shippingCost.toFixed(2)}`}</span>
                            </div>
                            <div className="border-t border-border-soft pt-3 flex justify-between font-serif text-2xl">
                                <span>Total</span>
                                <span data-testid="cart-total">
                                    ${(total + shippingCost).toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate("/checkout")}
                            className="mt-6 w-full bg-rose text-white hover:bg-rose-dark rounded-full px-8 py-4 transition-colors text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
                            data-testid="checkout-btn"
                        >
                            Finalizar pedido <ArrowRight strokeWidth={1.5} className="w-4 h-4" />
                        </button>
                        <Link
                            to="/catalogo"
                            className="mt-3 w-full block text-center text-xs tracking-[0.2em] uppercase text-taupe hover:text-rose"
                        >
                            Seguir explorando
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
